import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import HistoryIcon from '@mui/icons-material/History'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { alpha, Box, Chip, Divider, Typography, useTheme } from '@mui/material'
import { useDealData } from 'api/aws/deals/useDealData'
import { useEntitiesFromSearch } from 'api/search'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { PatchChange, PatchHistoryEntry } from 'types'
import { buildMasterAgreements, type VersionedDeal } from 'utils/agreementVersioning'
import { formatDateString } from 'utils/formatDayjsDateTime'

// ─── Status helpers (from VersionTimeline pattern) ────────────────────

const TERMINAL_STATUSES = ['Ended', 'Cancelled', 'Replaced']

const getStatusInfo = (deal: any) => {
  const stage = deal?.stage
  const status = deal?.dealStatus
  if (stage === 'Deal Won' && !TERMINAL_STATUSES.includes(status)) {
    return { label: 'Active', color: 'rgb(80, 200, 120)' }
  }
  if (status === 'Replaced') return { label: 'Replaced', color: 'rgb(239, 195, 124)' }
  if (status === 'Ended') return { label: 'Ended', color: 'rgba(220, 20, 60, 0.9)' }
  if (status === 'Cancelled') return { label: 'Cancelled', color: 'rgba(110, 114, 132, 0.5)' }
  return { label: status || stage || 'In Progress', color: renderColorByString(stage) }
}

// ─── Version type label ───────────────────────────────────────────────

function getVersionTypeLabel(version: string, prevVersion?: string): string {
  if (!prevVersion) return 'Initial'
  const [major, minor, patch] = version.split('.').map(Number)
  const [pMajor, pMinor] = (prevVersion || '0.0.0').split('.').map(Number)
  if (major > pMajor) return 'Major'
  if (minor > pMinor) return 'Minor'
  if (patch > (prevVersion?.split('.').map(Number)[2] ?? 0)) return 'Patch'
  return 'Update'
}

// ─── Change detail row ───────────────────────────────────────────────

const PriceChangeRow = ({ change }: { change: PatchChange }) => {
  const oldVal = Number(change.oldValue) || 0
  const newVal = Number(change.newValue) || 0
  const delta = newVal - oldVal
  const isIncrease = delta > 0

  if (change.field === 'price') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, px: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }} noWrap>
            {change.productName}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
          ${oldVal.toLocaleString()}
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap', color: isIncrease ? 'success.main' : 'error.main' }}>
          ${newVal.toLocaleString()}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 65, justifyContent: 'flex-end' }}>
          {isIncrease ? (
            <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
          )}
          <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: isIncrease ? 'success.main' : 'error.main' }}>
            {isIncrease ? '+' : ''}${delta.toLocaleString()}
          </Typography>
        </Box>
      </Box>
    )
  }

  // Date or other field change
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, px: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }} noWrap>
          {change.productName || change.field}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{change.field}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {String(change.oldValue)}
      </Typography>
      <ArrowForwardIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>
        {String(change.newValue)}
      </Typography>
    </Box>
  )
}

// ─── Patch history detail panel ──────────────────────────────────────

const PatchHistoryDetail = ({ entry, prevVersion }: { entry: PatchHistoryEntry; prevVersion?: string }) => {
  const versionType = getVersionTypeLabel(entry.patchVersion, prevVersion || entry.previousVersion)
  const priceChanges = entry.changes.filter(c => c.field === 'price')
  const dateChanges = entry.changes.filter(c => c.field !== 'price')

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          label={versionType}
          size="small"
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: versionType === 'Patch' ? 'info.main' : versionType === 'Minor' ? 'warning.main' : 'primary.main',
            color: 'white',
          }}
        />
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {new Date(entry.timestamp).toLocaleDateString()} at {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>

      {priceChanges.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', px: 1.5, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Price Changes
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {priceChanges.map((change, i) => (
              <Box key={i}>
                <PriceChangeRow change={change} />
                {i < priceChanges.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {dateChanges.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', px: 1.5, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Date Changes
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {dateChanges.map((change, i) => (
              <Box key={i}>
                <PriceChangeRow change={change} />
                {i < dateChanges.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {entry.changes.length === 0 && (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic', px: 1.5 }}>
          No detailed changes recorded
        </Typography>
      )}
    </Box>
  )
}

// ─── Version detail panel (right side) ───────────────────────────────

const VersionDetailPanel = ({ version, prevVersion }: { version: VersionedDeal; prevVersion?: VersionedDeal }) => {
  const deal = version.deal
  const patchHistory = (deal.patchHistory ?? []) as PatchHistoryEntry[]
  const statusInfo = getStatusInfo(deal)

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
      {/* Version header */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', color: 'text.primary' }}>
            v{version.semanticVersion}
          </Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusInfo.color }} />
          <Typography sx={{ fontSize: 13, color: statusInfo.color, fontWeight: 500 }}>
            {statusInfo.label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {deal.name} &middot; {deal.type}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {formatDateString(deal.startDate)} — {formatDateString(deal.endDate)}
        </Typography>
        {version.products.length > 0 && (
          <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>
            {version.products.length} service{version.products.length !== 1 ? 's' : ''}
            {deal.monthlyTotal ? ` · $${Number(deal.monthlyTotal).toLocaleString()}/mo` : ''}
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Patch history entries */}
      {patchHistory.length > 0 ? (
        <>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
            Change History
          </Typography>
          {patchHistory.map((entry, i) => (
            <PatchHistoryDetail
              key={i}
              entry={entry}
              prevVersion={i > 0 ? patchHistory[i - 1].patchVersion : undefined}
            />
          ))}
        </>
      ) : version.isRoot ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <HistoryIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1, opacity: 0.5 }} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Original agreement — no prior changes
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
            Version Created
          </Typography>
          {prevVersion && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                Created from v{prevVersion.semanticVersion}
              </Typography>

              {/* Product comparison summary */}
              {(() => {
                const prevNames = new Set(prevVersion.products.map(p => p.productName))
                const currNames = new Set(version.products.map(p => p.productName))
                const added = version.products.filter(p => !prevNames.has(p.productName))
                const removed = prevVersion.products.filter(p => !currNames.has(p.productName))
                const kept = version.products.filter(p => prevNames.has(p.productName))

                return (
                  <>
                    {added.length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'success.main' }}>
                          +{added.length} service{added.length !== 1 ? 's' : ''} added
                        </Typography>
                        {added.map((p, i) => (
                          <Typography key={i} sx={{ fontSize: 12, color: 'text.secondary', pl: 1.5 }}>
                            {p.productName} — ${Number(p.price || 0).toLocaleString()}/mo
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {removed.length > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'error.main' }}>
                          -{removed.length} service{removed.length !== 1 ? 's' : ''} removed
                        </Typography>
                        {removed.map((p, i) => (
                          <Typography key={i} sx={{ fontSize: 12, color: 'text.secondary', pl: 1.5, textDecoration: 'line-through' }}>
                            {p.productName}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {kept.length > 0 && added.length === 0 && removed.length === 0 && (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {kept.length} service{kept.length !== 1 ? 's' : ''} carried over (date renewal)
                      </Typography>
                    )}
                  </>
                )
              })()}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

// ─── Main component ─────────────────────────────────────────────────

const DealVersionsTab = () => {
  const { PK, SK } = useParams()
  const theme = useTheme()
  const navigate = useNavigate()
  const dealId = SK?.split('DEAL_')?.[1]
  const { deal } = useDealData(PK, SK, dealId)
  const { deals, products: allProducts } = useEntitiesFromSearch()

  // Build version chain
  const { agreement, currentIndex } = useMemo(() => {
    const empty = { agreement: null, currentIndex: 0 }
    if (!PK || !deal?.deal_uuid) return empty

    const orgDeals = deals?.filter(d => d?.PK === PK) ?? []
    const orgProducts = allProducts?.filter(p => p?.PK === PK) ?? []
    const { agreements } = buildMasterAgreements(orgDeals, orgProducts)

    const match =
      (deal.masterAgreementId
        ? agreements.find(a => a.masterAgreementId === deal.masterAgreementId)
        : null) ?? agreements.find(a => a.versions.some(v => v.deal?.deal_uuid === deal.deal_uuid))

    if (!match) return empty

    const idx = match.versions.findIndex(v => v.deal?.deal_uuid === deal.deal_uuid)
    return { agreement: match, currentIndex: idx >= 0 ? idx : match.versions.length - 1 }
  }, [PK, deal?.deal_uuid, deal?.masterAgreementId, deals, allProducts])

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const effectiveIndex = selectedIndex ?? currentIndex
  const versions = agreement?.versions ?? []
  const selectedVersion = versions[effectiveIndex]

  const handleSelectVersion = (index: number) => {
    setSelectedIndex(index)
    // Navigate to that deal's version tab if it's a different deal
    const targetDeal = versions[index]?.deal
    if (targetDeal && targetDeal.SK !== SK) {
      navigate(`/deals/${targetDeal.PK}/${targetDeal.SK}/versions`)
    }
  }

  // No versioning data
  if (!agreement || versions.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5, opacity: 0.4 }} />
          <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 500 }}>
            No version history available
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>
            This deal is not part of a versioned agreement chain
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left: Version timeline */}
      <Box
        sx={{
          width: 280,
          minWidth: 280,
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
          py: 1,
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
            Version History
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {versions.length} version{versions.length !== 1 ? 's' : ''} &middot; {agreement.agreementFamily}
          </Typography>
        </Box>

        {/* Version items — newest first */}
        {[...versions].reverse().map((v, reversedIdx) => {
          const actualIndex = versions.length - 1 - reversedIdx
          const isSelected = actualIndex === effectiveIndex
          const isLatest = actualIndex === versions.length - 1
          const isCurrent = v.deal?.deal_uuid === deal?.deal_uuid
          const statusInfo = getStatusInfo(v.deal)
          const patchCount = ((v.deal.patchHistory ?? []) as PatchHistoryEntry[]).length

          return (
            <Box
              key={v.deal.deal_uuid}
              onClick={() => handleSelectVersion(actualIndex)}
              sx={{
                position: 'relative',
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                borderLeft: '3px solid',
                borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                bgcolor: isSelected
                  ? alpha(theme.palette.text.primary, 0.04)
                  : 'transparent',
                '&:hover': {
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                },
                transition: 'all 0.15s ease',
              }}
            >
              {/* Version number + badges */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`v${v.semanticVersion}`}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    bgcolor: isSelected ? 'primary.main' : 'action.selected',
                    color: isSelected ? 'primary.contrastText' : 'text.primary',
                  }}
                />
                {isLatest && (
                  <Chip label="Latest" size="small" variant="outlined" color="success" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                )}
                {isCurrent && !isLatest && (
                  <Chip label="Current" size="small" variant="outlined" color="info" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                )}
              </Box>

              {/* Deal type */}
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary', mb: 0.25 }}>
                {v.deal.type}
              </Typography>

              {/* Date range */}
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {formatDateString(v.deal.startDate)} — {formatDateString(v.deal.endDate)}
              </Typography>

              {/* Status + patch count */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusInfo.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 11, color: statusInfo.color, fontWeight: 500 }}>
                  {statusInfo.label}
                </Typography>
                {patchCount > 0 && (
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', ml: 0.5 }}>
                    &middot; {patchCount} change{patchCount !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>

              {/* Product count */}
              <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>
                {v.products.length} service{v.products.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )
        })}
      </Box>

      {/* Right: Version detail */}
      {selectedVersion ? (
        <VersionDetailPanel
          version={selectedVersion}
          prevVersion={effectiveIndex > 0 ? versions[effectiveIndex - 1] : undefined}
        />
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: 'text.disabled' }}>Select a version to view details</Typography>
        </Box>
      )}
    </Box>
  )
}

export default DealVersionsTab
